/** Mirrors the backend's `@maxItems` on the keyset request's `simulation_output_ids`. */
export const MAX_KEYSET_SIMULATION_OUTPUT_IDS = 10_000

interface ConversationsSimulationOutputRestriction {
  /** Ids to send as `simulation_output_ids`; undefined applies no restriction. */
  simulationOutputIds: string[] | undefined
  /** True when the active restriction can match no conversation at all. */
  matchesNothing: boolean
  /** Restricted id count (after any cluster intersection) before the request cap, when the cap dropped some. */
  truncatedFromCount: number | null
}

/**
 * Combines the human review project filter with a visualizer cluster filter
 * into the one `simulation_output_ids` list the keyset request accepts.
 *
 * `projectLinkedIds` is undefined when no project is selected. An empty list
 * there means the selected projects hold no conversations, which must match
 * nothing — the backend treats an empty `simulation_output_ids` as no
 * restriction. Over the request cap, the most recently linked ids are kept.
 */
export function resolveConversationsSimulationOutputRestriction({
  projectLinkedIds,
  clusterIds,
}: {
  projectLinkedIds: string[] | undefined
  clusterIds: string[] | undefined
}): ConversationsSimulationOutputRestriction {
  const hasCluster = clusterIds !== undefined && clusterIds.length > 0
  if (projectLinkedIds === undefined) {
    return {
      simulationOutputIds: hasCluster ? clusterIds : undefined,
      matchesNothing: false,
      truncatedFromCount: null,
    }
  }

  let restrictedIds = projectLinkedIds
  if (hasCluster) {
    const clusterIdSet = new Set(clusterIds)
    restrictedIds = projectLinkedIds.filter((id) => clusterIdSet.has(id))
  }
  if (restrictedIds.length === 0) {
    return {
      simulationOutputIds: undefined,
      matchesNothing: true,
      truncatedFromCount: null,
    }
  }
  if (restrictedIds.length > MAX_KEYSET_SIMULATION_OUTPUT_IDS) {
    return {
      simulationOutputIds: restrictedIds.slice(
        -MAX_KEYSET_SIMULATION_OUTPUT_IDS,
      ),
      matchesNothing: false,
      truncatedFromCount: restrictedIds.length,
    }
  }
  return {
    simulationOutputIds: restrictedIds,
    matchesNothing: false,
    truncatedFromCount: null,
  }
}
